import React, { useState, useEffect, useContext } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import {
  FaSearch,
  FaSpinner,
  FaMapMarkerAlt,
  FaBuilding,
  FaFilter,
  FaEye,
  FaTrash,
  FaEdit,
  FaPlus,
  FaExternalLinkAlt,
  FaDownload,
  FaSortAmountDown,
  FaSortAmountUp,
  FaGlobe,
  FaLinkedin,
  FaIndustry,
  FaRecycle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";

// ===============================================================
// Enriched Research Data Viewer Component
// View and search company research data with enrichment info
// ===============================================================

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------
const COLLECTION_NAME = "research";
const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100];
const DEFAULT_ITEMS_PER_PAGE = 50;
const BATCH_FETCH_SIZE = 50; // Load only 50 records at a time
const ENABLE_LOGGING = true;

// Admin UIDs for delete functionality
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];

// Debug logging helper
const log = (...args) => {
  if (ENABLE_LOGGING) {
    console.log("[ResearchViewer]", ...args);
  }
};

// Format number with commas
const formatNumber = (num) => {
  if (num === undefined || num === null) return "—";
  return new Intl.NumberFormat().format(num);
};

// Truncate text with ellipsis
const truncateText = (text, maxLength = 100) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

// Get confidence badge color
const getConfidenceBadge = (confidence) => {
  switch (confidence?.toLowerCase()) {
    case "high":
      return "bg-green-100 text-green-800";
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "low":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// ===============================================================
//                       MAIN COMPONENT
// ===============================================================
const EnrichedResearchViewer = ({ teamId }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  // ---------------------------------------------------------------
  // State Management
  // ---------------------------------------------------------------
  // Data state
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [remoteDocs, setRemoteDocs] = useState([]);
  const [totalCount, setTotalCount] = useState(0); // Total count for pagination
  const [lastDoc, setLastDoc] = useState(null); // For pagination
  const [hasNextPage, setHasNextPage] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter state
  const [filters, setFilters] = useState({
    state: "",
    region: "",
    industrySegment: "",
    territoryOwner: "",
    keyAccountOnly: false,
    contactAvailableOnly: false,
    minTonnage: "",
    maxTonnage: "",
    websiteConfidence: "",
  });

  // Sort state
  const [sortField, setSortField] = useState("companyName");
  const [sortDirection, setSortDirection] = useState("asc");

  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // CRUD state for admin operations
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Stats for UI
  const [stats, setStats] = useState({
    totalRecords: 0,
    totalTonnage: 0,
    statesCount: 0,
    keyAccountsCount: 0,
    enrichedCount: 0,
  });

  // ---------------------------------------------------------------
  // Initial Data Load
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!user || !teamId) {
      log("No user or teamId, skipping data load");
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      log("Starting data load for team:", teamId);

      try {
        const collectionRef = collection(db, "teams", teamId, COLLECTION_NAME);

        // Get total count for stats (lightweight count)
        const countSnapshot = await getDocs(collectionRef);
        const totalRecords = countSnapshot.size;
        setTotalCount(totalRecords);

        // Load first page only (50 records)
        const q = query(
          collectionRef,
          orderBy("companyNameNormalized"),
          limit(BATCH_FETCH_SIZE)
        );

        const snapshot = await getDocs(q);
        const initialData = [];

        snapshot.forEach((doc) => {
          initialData.push({ id: doc.id, ...doc.data() });
        });

        setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasNextPage(snapshot.docs.length === BATCH_FETCH_SIZE);

        setAllData(initialData);
        setFilteredData(initialData);

        // Calculate stats from all records for accurate totals
        const allRecordsData = countSnapshot.docs.map((doc) => doc.data());
        calculateStats(allRecordsData);

        toast.success(
          `Loaded ${initialData.length} of ${totalRecords} research records`
        );
        log(
          "Initial data load complete:",
          initialData.length,
          "of",
          totalRecords,
          "records"
        );
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, teamId]);

  // Load next page
  const loadNextPage = async () => {
    if (!lastDoc || !hasNextPage || loading) return;

    setLoading(true);
    try {
      const collectionRef = collection(db, "teams", teamId, COLLECTION_NAME);
      const q = query(
        collectionRef,
        orderBy("companyNameNormalized"),
        startAfter(lastDoc),
        limit(BATCH_FETCH_SIZE)
      );
      const snapshot = await getDocs(q);

      const newData = [];
      snapshot.forEach((doc) => {
        newData.push({ id: doc.id, ...doc.data() });
      });

      setAllData((prev) => [...prev, ...newData]);
      setFilteredData((prev) => [...prev, ...newData]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasNextPage(snapshot.docs.length === BATCH_FETCH_SIZE);

      toast.success(`Loaded ${newData.length} more records`);
    } catch (error) {
      console.error("Error loading more data:", error);
      toast.error("Failed to load more data");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------
  // Reactive Search (array-contains)
  // ---------------------------------------------------------------
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term || term.includes(" ") || term.length > 20) {
      setRemoteDocs([]);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        const collectionRef = collection(db, "teams", teamId, COLLECTION_NAME);
        const q = query(
          collectionRef,
          where("searchArray", "array-contains", term),
          limit(200)
        );
        const snapshot = await getDocs(q);
        const rows = [];
        snapshot.forEach((doc) => rows.push({ id: doc.id, ...doc.data() }));

        if (!cancelled) {
          setRemoteDocs(rows);
          log("Search found", rows.length, "results for:", term);
        }
      } catch (e) {
        log("Search error:", e);
      }
    };

    run();
    return () => (cancelled = true);
  }, [searchTerm, teamId]);

  // ---------------------------------------------------------------
  // Stats Calculation
  // ---------------------------------------------------------------
  const calculateStats = (data) => {
    const stats = {
      totalRecords: data.length,
      totalTonnage: 0,
      statesCount: new Set(),
      keyAccountsCount: 0,
      enrichedCount: 0,
    };

    data.forEach((item) => {
      // Total tonnage
      stats.totalTonnage += item.wasteMetrics?.totalTons || 0;

      // Unique states
      if (item.location?.state) {
        stats.statesCount.add(item.location.state);
      }

      // Key accounts
      if (item.keyNationalAccount) {
        stats.keyAccountsCount++;
      }

      // Enriched (has website)
      if (item.enrichment?.companyWebsite) {
        stats.enrichedCount++;
      }
    });

    stats.statesCount = stats.statesCount.size;
    setStats(stats);
    log("Stats calculated:", stats);
  };

  // ---------------------------------------------------------------
  // Manual Search
  // ---------------------------------------------------------------
  const performSearch = async () => {
    if (!searchTerm.trim()) {
      toast.info("Please enter a search term");
      return;
    }

    setSearching(true);
    log("Performing search for:", searchTerm);

    try {
      const searchLower = searchTerm.toLowerCase().trim();
      const collectionRef = collection(db, "teams", teamId, COLLECTION_NAME);

      const q = query(
        collectionRef,
        where("searchArray", "array-contains", searchLower),
        limit(500)
      );

      const snapshot = await getDocs(q);
      const results = [];

      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });

      log("Search returned", results.length, "results");
      toast.info(`Found ${results.length} results`);
      setRemoteDocs(results);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // ---------------------------------------------------------------
  // Filtering and Sorting
  // ---------------------------------------------------------------
  useEffect(() => {
    log("Applying filters and sorting");

    // Merge local and remote docs
    let data = [...allData, ...remoteDocs];
    const seen = new Set();
    data = data.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });

    // Apply text search filter (client-side)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter((item) => {
        return (
          item.companyName?.toLowerCase().includes(searchLower) ||
          item.location?.city?.toLowerCase().includes(searchLower) ||
          item.location?.state?.toLowerCase().includes(searchLower) ||
          item.location?.zipCode?.includes(searchTerm) ||
          item.territoryOwner?.toLowerCase().includes(searchLower) ||
          item.enrichment?.companySummary?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply filters
    if (filters.state) {
      data = data.filter((item) => item.location?.state === filters.state);
    }
    if (filters.region) {
      data = data.filter((item) => item.region === filters.region);
    }
    if (filters.industrySegment) {
      data = data.filter(
        (item) => item.industrySegment === filters.industrySegment
      );
    }
    if (filters.territoryOwner) {
      data = data.filter(
        (item) => item.territoryOwner === filters.territoryOwner
      );
    }
    if (filters.keyAccountOnly) {
      data = data.filter((item) => item.keyNationalAccount);
    }
    if (filters.contactAvailableOnly) {
      data = data.filter((item) => item.contactAvailable);
    }
    if (filters.minTonnage) {
      const min = parseFloat(filters.minTonnage);
      data = data.filter((item) => (item.wasteMetrics?.totalTons || 0) >= min);
    }
    if (filters.maxTonnage) {
      const max = parseFloat(filters.maxTonnage);
      data = data.filter((item) => (item.wasteMetrics?.totalTons || 0) <= max);
    }
    if (filters.websiteConfidence) {
      data = data.filter(
        (item) =>
          item.enrichment?.websiteConfidence?.toLowerCase() ===
          filters.websiteConfidence.toLowerCase()
      );
    }

    // Apply sorting
    data.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case "companyName":
          aVal = a.companyName || "";
          bVal = b.companyName || "";
          break;
        case "state":
          aVal = a.location?.state || "";
          bVal = b.location?.state || "";
          break;
        case "city":
          aVal = a.location?.city || "";
          bVal = b.location?.city || "";
          break;
        case "totalTons":
          aVal = a.wasteMetrics?.totalTons || 0;
          bVal = b.wasteMetrics?.totalTons || 0;
          break;
        case "squareFeet":
          aVal = a.facilityInfo?.squareFeet || 0;
          bVal = b.facilityInfo?.squareFeet || 0;
          break;
        default:
          aVal = a[sortField] || "";
          bVal = b[sortField] || "";
      }

      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredData(data);
    setCurrentPage(1);
    log("Filtered data count:", data.length);
  }, [searchTerm, filters, sortField, sortDirection, allData, remoteDocs]);

  // ---------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayData(filteredData.slice(startIndex, endIndex));
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ---------------------------------------------------------------
  // Export functionality
  // ---------------------------------------------------------------
  const exportToCSV = () => {
    log("Exporting", filteredData.length, "records to CSV");

    // Create CSV header
    const headers = [
      "Registry ID",
      "Company Name",
      "Territory Owner",
      "Region",
      "Address",
      "City",
      "State",
      "ZIP",
      "Industry Segment",
      "Square Feet",
      "Employee Count",
      "Total Tons",
      "Key Account",
      "Contact Available",
      "Company Website",
      "LinkedIn URL",
      "Website Confidence",
      "Summary",
    ];

    // Create CSV rows
    const rows = filteredData.map((item) => [
      item.registryId || "",
      item.companyName || "",
      item.territoryOwner || "",
      item.region || "",
      item.location?.street || "",
      item.location?.city || "",
      item.location?.state || "",
      item.location?.zipCode || "",
      item.industrySegment || "",
      item.facilityInfo?.squareFeet || "",
      item.facilityInfo?.employeeCount || "",
      item.wasteMetrics?.totalTons || "",
      item.keyNationalAccount ? "Yes" : "No",
      item.contactAvailable ? "Yes" : "No",
      item.enrichment?.companyWebsite || "",
      item.enrichment?.linkedInUrl || "",
      item.enrichment?.websiteConfidence || "",
      (item.enrichment?.companySummary || "").replace(/"/g, '""'), // Escape quotes
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    // Create download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `research_export_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${filteredData.length} records`);
  };

  // ---------------------------------------------------------------
  // Helper functions
  // ---------------------------------------------------------------
  const clearFilters = () => {
    setFilters({
      state: "",
      region: "",
      industrySegment: "",
      territoryOwner: "",
      keyAccountOnly: false,
      contactAvailableOnly: false,
      minTonnage: "",
      maxTonnage: "",
      websiteConfidence: "",
    });
    setSearchTerm("");
    setRemoteDocs([]);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const viewDetails = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  const getGoogleMapsUrl = (location) => {
    if (!location) return "";
    const address =
      location.full ||
      `${location.city}, ${location.state} ${location.zipCode}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  };

  // Get unique values for filter dropdowns
  const uniqueStates = [
    ...new Set(allData.map((item) => item.location?.state).filter(Boolean)),
  ].sort();
  const uniqueRegions = [
    ...new Set(allData.map((item) => item.region).filter(Boolean)),
  ].sort();
  const uniqueIndustries = [
    ...new Set(allData.map((item) => item.industrySegment).filter(Boolean)),
  ].sort();
  const uniqueOwners = [
    ...new Set(allData.map((item) => item.territoryOwner).filter(Boolean)),
  ].sort();

  // ===============================================================
  //                          RENDER
  // ===============================================================

  // Early return if no team ID - placed after all hooks
  if (!teamId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg">
          No team selected. Please ensure you have access to a team.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Enriched Research Data</h1>
        <p className="text-gray-600">
          Browse company data with website and LinkedIn information
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">
            {formatNumber(stats.totalRecords)}
          </div>
          <div className="text-sm text-gray-600">Total Companies</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {formatNumber(Math.round(stats.totalTonnage))}
          </div>
          <div className="text-sm text-gray-600">Total Tonnage</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-purple-600">
            {stats.statesCount}
          </div>
          <div className="text-sm text-gray-600">States</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-orange-600">
            {stats.keyAccountsCount}
          </div>
          <div className="text-sm text-gray-600">Key Accounts</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-indigo-600">
            {stats.enrichedCount}
          </div>
          <div className="text-sm text-gray-600">With Websites</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        {/* Search Bar */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <input
              type="text"
              className="w-full px-4 py-2 pl-10 pr-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search by company, city, state, or summary..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && performSearch()}
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
          <button
            onClick={performSearch}
            disabled={searching}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            {searching ? <FaSpinner className="animate-spin" /> : <FaSearch />}
            Search
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <select
            value={filters.state}
            onChange={(e) => setFilters({ ...filters, state: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All States</option>
            {uniqueStates.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>

          <select
            value={filters.region}
            onChange={(e) => setFilters({ ...filters, region: e.target.value })}
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Regions</option>
            {uniqueRegions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>

          <select
            value={filters.industrySegment}
            onChange={(e) =>
              setFilters({ ...filters, industrySegment: e.target.value })
            }
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Industries</option>
            {uniqueIndustries.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>

          <select
            value={filters.territoryOwner}
            onChange={(e) =>
              setFilters({ ...filters, territoryOwner: e.target.value })
            }
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Owners</option>
            {uniqueOwners.map((owner) => (
              <option key={owner} value={owner}>
                {owner}
              </option>
            ))}
          </select>

          <select
            value={filters.websiteConfidence}
            onChange={(e) =>
              setFilters({ ...filters, websiteConfidence: e.target.value })
            }
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Confidence Levels</option>
            <option value="high">High Confidence</option>
            <option value="medium">Medium Confidence</option>
            <option value="low">Low Confidence</option>
          </select>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filters.keyAccountOnly}
              onChange={(e) =>
                setFilters({ ...filters, keyAccountOnly: e.target.checked })
              }
              className="rounded"
            />
            <span className="text-sm">Key Accounts Only</span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={filters.contactAvailableOnly}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  contactAvailableOnly: e.target.checked,
                })
              }
              className="rounded"
            />
            <span className="text-sm">Contact Available</span>
          </label>

          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            <FaFilter className="inline mr-2" />
            Clear
          </button>
        </div>

        {/* Results count and export */}
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-600">
            Showing {displayData.length} of {filteredData.length} results
            {filteredData.length < allData.length &&
              ` (filtered from ${allData.length} total)`}
          </div>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <FaDownload />
            Export CSV
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading data...</p>
          </div>
        ) : displayData.length === 0 ? (
          <div className="p-8 text-center">
            <FaFilter className="text-4xl text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No data matches your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort("companyName")}
                      className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
                    >
                      Company
                      {sortField === "companyName" &&
                        (sortDirection === "asc" ? (
                          <FaSortAmountUp />
                        ) : (
                          <FaSortAmountDown />
                        ))}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort("city")}
                      className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900"
                    >
                      Location
                      {sortField === "city" &&
                        (sortDirection === "asc" ? (
                          <FaSortAmountUp />
                        ) : (
                          <FaSortAmountDown />
                        ))}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">Industry</th>
                  <th className="px-4 py-3 text-left">Owner</th>
                  <th className="px-4 py-3 text-left">Website</th>
                  <th className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleSort("totalTons")}
                      className="flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900 ml-auto"
                    >
                      Total Tons
                      {sortField === "totalTons" &&
                        (sortDirection === "asc" ? (
                          <FaSortAmountUp />
                        ) : (
                          <FaSortAmountDown />
                        ))}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {displayData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900">
                          {item.companyName}
                          {item.keyNationalAccount && (
                            <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                              Key Account
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          Doc ID: {item.id}
                        </div>
                        {item.registryId && (
                          <div className="text-sm text-gray-500">
                            Registry ID: {item.registryId}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-1">
                        <FaMapMarkerAlt className="text-gray-400 mt-1 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium">
                            {item.location?.city}, {item.location?.state}{" "}
                            {item.location?.zipCode}
                          </div>
                          {item.location?.street && (
                            <div className="text-xs text-gray-500">
                              {item.location.street}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {item.industrySegment || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {item.territoryOwner || "—"}
                        {item.contactAvailable && (
                          <span className="block text-xs text-green-600">
                            Contact available
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {item.enrichment?.companyWebsite && (
                          <div className="flex items-center gap-2">
                            <FaGlobe className="text-blue-500" />
                            <a
                              href={item.enrichment.companyWebsite}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline truncate max-w-xs"
                            >
                              {item.enrichment.companyWebsite.replace(
                                /^https?:\/\//,
                                ""
                              )}
                            </a>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${getConfidenceBadge(
                                item.enrichment.websiteConfidence
                              )}`}
                            >
                              {item.enrichment.websiteConfidence}
                            </span>
                          </div>
                        )}
                        {item.enrichment?.linkedInUrl && (
                          <div className="flex items-center gap-2">
                            <FaLinkedin className="text-blue-700" />
                            <a
                              href={item.enrichment.linkedInUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline"
                            >
                              LinkedIn
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm font-medium">
                        {formatNumber(item.wasteMetrics?.totalTons)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => viewDetails(item)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View details"
                        >
                          <FaEye />
                        </button>
                        <a
                          href={getGoogleMapsUrl(item.location)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                          title="View on map"
                        >
                          <FaMapMarkerAlt />
                        </a>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => {
                                setEditingItem(item);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit company"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Delete ${item.companyName}?\n\nThis action cannot be undone.`
                                  )
                                ) {
                                  // TODO: Implement delete
                                  toast.info(
                                    "Delete functionality coming soon"
                                  );
                                }
                              }}
                              className="text-red-600 hover:text-red-800"
                              title="Delete company"
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Load More & Pagination */}
      {filteredData.length > 0 && (
        <div className="mt-6 space-y-4">
          {/* Load More Button */}
          {hasNextPage && (
            <div className="text-center">
              <button
                onClick={loadNextPage}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    Load More Records ({allData.length} of {totalCount} loaded)
                  </>
                )}
              </button>
            </div>
          )}

          {/* Pagination Info */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-600">
                | Loaded {allData.length} of {totalCount} total records
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="px-4 py-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Controls */}
      {isAdmin && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3 text-yellow-800">
            Admin Controls
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
            >
              <FaBuilding className="mr-2" />
              Add New Company
            </button>
            <button
              onClick={() => {
                // TODO: Implement bulk operations
                toast.info("Bulk operations coming soon");
              }}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
            >
              <FaDownload className="mr-2" />
              Bulk Operations
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{selectedItem.companyName}</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Basic Information
                  </h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600">Document ID</dt>
                      <dd className="font-medium font-mono text-xs break-all">
                        {selectedItem.id}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Registry ID</dt>
                      <dd className="font-medium">
                        {selectedItem.registryId || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Territory Owner</dt>
                      <dd className="font-medium">
                        {selectedItem.territoryOwner || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Region</dt>
                      <dd className="font-medium">
                        {selectedItem.region || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">
                        Industry Segment
                      </dt>
                      <dd className="font-medium">
                        {selectedItem.industrySegment || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">
                        Key National Account
                      </dt>
                      <dd className="font-medium">
                        {selectedItem.keyNationalAccount ? (
                          <span className="text-green-600">Yes</span>
                        ) : (
                          <span className="text-gray-500">No</span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Location Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Location</h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600">Address</dt>
                      <dd className="font-medium">
                        {selectedItem.location?.street || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">City</dt>
                      <dd className="font-medium">
                        {selectedItem.location?.city || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">County</dt>
                      <dd className="font-medium">
                        {selectedItem.location?.county || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">State</dt>
                      <dd className="font-medium">
                        {selectedItem.location?.state || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">ZIP Code</dt>
                      <dd className="font-medium">
                        {selectedItem.location?.zipCode || "—"}
                      </dd>
                    </div>
                  </dl>
                  <a
                    href={getGoogleMapsUrl(selectedItem.location)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <FaMapMarkerAlt />
                    View on Google Maps
                    <FaExternalLinkAlt className="text-sm" />
                  </a>
                </div>

                {/* Facility Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Facility Information
                  </h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600">Facility Type</dt>
                      <dd className="font-medium">
                        {selectedItem.facilityType || "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Square Feet</dt>
                      <dd className="font-medium">
                        {formatNumber(selectedItem.facilityInfo?.squareFeet)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Employee Count</dt>
                      <dd className="font-medium">
                        {formatNumber(selectedItem.facilityInfo?.employeeCount)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">
                        Closest Facility
                      </dt>
                      <dd className="font-medium">
                        {selectedItem.facilityInfo?.closestFacilityName || "—"}
                        {selectedItem.facilityInfo?.closestFacilityMiles && (
                          <span className="text-sm text-gray-500">
                            {" "}
                            ({
                              selectedItem.facilityInfo.closestFacilityMiles
                            }{" "}
                            mi)
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Waste Metrics */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Waste Metrics</h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600">PW Solids</dt>
                      <dd className="font-medium">
                        {formatNumber(selectedItem.wasteMetrics?.pwSolidsTons)}{" "}
                        tons
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">WWT</dt>
                      <dd className="font-medium">
                        {formatNumber(selectedItem.wasteMetrics?.wwtTons)} tons
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Drums</dt>
                      <dd className="font-medium">
                        {formatNumber(selectedItem.wasteMetrics?.drumsTons)}{" "}
                        tons
                      </dd>
                    </div>
                    <div className="pt-2 border-t">
                      <dt className="text-sm text-gray-600 font-semibold">
                        Total
                      </dt>
                      <dd className="text-lg font-bold text-blue-600">
                        {formatNumber(selectedItem.wasteMetrics?.totalTons)}{" "}
                        tons
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* Enrichment Data */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold mb-3">Enrichment Data</h3>

                {/* Website and LinkedIn */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {selectedItem.enrichment?.companyWebsite && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <FaGlobe className="text-blue-600 text-xl" />
                      <div className="flex-1">
                        <a
                          href={selectedItem.enrichment.companyWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {selectedItem.enrichment.companyWebsite}
                        </a>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-600">
                            Confidence:
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${getConfidenceBadge(
                              selectedItem.enrichment.websiteConfidence
                            )}`}
                          >
                            {selectedItem.enrichment.websiteConfidence}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedItem.enrichment?.linkedInUrl && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <FaLinkedin className="text-blue-700 text-xl" />
                      <div className="flex-1">
                        <a
                          href={selectedItem.enrichment.linkedInUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline font-medium"
                        >
                          LinkedIn Profile
                        </a>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-600">
                            Confidence:
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${getConfidenceBadge(
                              selectedItem.enrichment.linkedInConfidence
                            )}`}
                          >
                            {selectedItem.enrichment.linkedInConfidence}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Summary and Other Info */}
                <div className="space-y-4">
                  {selectedItem.enrichment?.companySummary && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Company Summary
                      </h4>
                      <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">
                        {selectedItem.enrichment.companySummary}
                      </p>
                    </div>
                  )}

                  {selectedItem.enrichment?.estManufacturingLocations && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Estimated Manufacturing Locations
                      </h4>
                      <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">
                        {selectedItem.enrichment.estManufacturingLocations}
                      </p>
                    </div>
                  )}

                  {selectedItem.enrichment?.wasteGenerationProfile && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <FaRecycle className="text-green-600" />
                        Waste Generation Profile
                      </h4>
                      <p className="text-gray-600 bg-green-50 p-3 rounded-lg">
                        {selectedItem.enrichment.wasteGenerationProfile}
                      </p>
                    </div>
                  )}

                  {selectedItem.enrichment?.additionalNotes && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">
                        Additional Notes
                      </h4>
                      <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">
                        {selectedItem.enrichment.additionalNotes}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold mb-3">Metadata</h3>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-600">Document ID</dt>
                    <dd className="font-mono text-xs">{selectedItem.id}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-600">Imported At</dt>
                    <dd>
                      {selectedItem.metadata?.importedAt
                        ? new Date(
                            selectedItem.metadata.importedAt
                          ).toLocaleString()
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-600">Source Row</dt>
                    <dd>{selectedItem.metadata?.sourceRowNumber || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-600">Search Terms</dt>
                    <dd>{selectedItem.searchArray?.length || 0} terms</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrichedResearchViewer;
